import { useCallback, useEffect, useMemo, useState } from "react"
import { useLocation, useNavigate } from "react-router-dom"

import useGa4Property from "../hooks/useGa4Property"
import { supabase } from "../supabaseClient"
import { useUser } from "../UserContext"
import { getValidAccessToken } from "../utils/ga4Tokens"
import { formatNumber } from "../utils/formatters"

export default function GA4HostnameDetails() {
  const user = useUser()
  const navigate = useNavigate()
  const { search } = useLocation()
  const propertyId = new URLSearchParams(search).get("id")
  const hostnameId = new URLSearchParams(search).get("hostnameId")
  const normalizedId = propertyId?.replace(/^properties\//, "") || ""
  const [hostnameRow, setHostnameRow] = useState(null)
  const [loading, setLoading] = useState(true)
  const [notesDraft, setNotesDraft] = useState("")
  const [savingNotes, setSavingNotes] = useState(false)
  const [noteError, setNoteError] = useState(null)
  const [events, setEvents] = useState([])
  const [eventNote, setEventNote] = useState(null)
  const [eventError, setEventError] = useState(null)
  const [eventsLoading, setEventsLoading] = useState(false)
  const [hostnameError, setHostnameError] = useState(null)
  const {
    property: propInfo,
    loading: propertyLoading,
    error: propertyError,
  } = useGa4Property(propertyId)

  const loadEvents = useCallback(async (hostname) => {
    setEventsLoading(true)
    setEventError(null)
    setEventNote(null)
    try {
      const accessToken = await getValidAccessToken(supabase, user)
      if (!accessToken) {
        setEventError("Missing GA4 access token")
        setEvents([])
        setEventsLoading(false)
        return
      }
      const { data, error } = await supabase.functions.invoke("ga4-proxy", {
        body: {
          propertyId: normalizedId,
          type: "hostname-event-breakdown",
          hostname,
        },
        headers: { "x-ga4-authorization": `Bearer ${accessToken}` },
      })
      if (error) {
        const message =
          error.context?.body?.error?.message || error.message || "Failed to load events"
        setEventError(message)
        setEvents([])
      } else if (data && typeof data.note === "string") {
        setEventNote(data.note)
        setEvents([])
      } else if (Array.isArray(data)) {
        setEvents(data)
      } else {
        setEvents([])
      }
    } catch (err) {
      console.error("Failed to fetch hostname events", err)
      setEventError(err?.message || "Failed to load events")
      setEvents([])
    } finally {
      setEventsLoading(false)
    }
  }, [normalizedId, user])

  useEffect(() => {
    if (!propertyId || !hostnameId) {
      setHostnameRow(null)
      setNotesDraft("")
      setEvents([])
      setEventNote(null)
      setHostnameError(null)
      setLoading(false)
      return
    }

    if (!user) {
      setLoading(true)
      return
    }

    if (propertyLoading) return

    if (propertyError || !propInfo?.id) {
      setHostnameRow(null)
      setNotesDraft("")
      setEvents([])
      setEventNote(null)
      setHostnameError(propertyError || null)
      setLoading(false)
      return
    }

    let cancelled = false

    const fetchData = async () => {
      setLoading(true)
      setHostnameError(null)
      try {
        const { data: row, error } = await supabase
          .from("ga4_hostname_audit_results")
          .select(
            "id, hostname, first_detected, last_detected, session_count, expected, notes, event_owners"
          )
          .eq("ga4_property_id", propInfo.id)
          .eq("id", hostnameId)
          .maybeSingle()
        if (error) throw error
        if (cancelled) return
        setHostnameRow(row)
        setNotesDraft(row?.notes || "")
        if (row?.hostname) {
          await loadEvents(row.hostname)
        } else {
          setEvents([])
          setEventNote(null)
        }
      } catch (err) {
        if (cancelled) return
        console.error("Failed to load hostname details", err)
        setHostnameRow(null)
        setNotesDraft("")
        setEvents([])
        setEventNote(null)
        setHostnameError(err)
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    fetchData()

    return () => {
      cancelled = true
    }
  }, [user, propertyId, hostnameId, propInfo?.id, propertyLoading, propertyError, loadEvents])

  const saveNotes = async () => {
    if (!hostnameRow?.id) return
    setSavingNotes(true)
    setNoteError(null)
    try {
      const { error } = await supabase
        .from("ga4_hostname_audit_results")
        .update({ notes: notesDraft })
        .eq("id", hostnameRow.id)
      if (error) throw error
      setHostnameRow((prev) => (prev ? { ...prev, notes: notesDraft } : prev))
    } catch (err) {
      console.error("Failed to save hostname notes", err)
      setNoteError(err?.message || "Failed to save notes")
    } finally {
      setSavingNotes(false)
    }
  }

  const backToAudit = () => {
    navigate(`/ga4-property/hostname-audit?id=${encodeURIComponent(propertyId || "")}`)
  }

  const eventRows = useMemo(() => events, [events])
  const canSaveNotes = (hostnameRow?.notes || "") !== notesDraft
  const formatEventOwners = (owners) => {
    if (!Array.isArray(owners)) return "—"
    const filtered = owners
      .filter((owner) => typeof owner === "string")
      .map((owner) => owner.trim())
      .filter((owner) => owner && owner !== "(not set)")
    if (!filtered.length) return "—"
    return filtered.join(", ")
  }

  return (
    <div className="page-container">
      <h2>Hostname Details</h2>
      <button onClick={backToAudit}>Back</button>
      {loading ? (
        <p>Loading...</p>
      ) : propertyError ? (
        <p style={{ marginTop: "1rem", color: "#b00020" }}>
          Failed to load property details.
        </p>
      ) : hostnameError ? (
        <p style={{ marginTop: "1rem", color: "#b00020" }}>
          Failed to load hostname details.
        </p>
      ) : !hostnameRow ? (
        <p style={{ marginTop: "1rem" }}>Hostname not found for this property.</p>
      ) : (
        <>
          {propInfo && (
            <div style={{ marginTop: "1rem" }}>
              <h3 style={{ margin: 0 }}>
                {propInfo.property_name}
                <span
                  style={{
                    fontWeight: "normal",
                    fontSize: "0.9em",
                    marginLeft: "0.5rem",
                  }}
                >
                  ({propInfo.property_id})
                </span>
              </h3>
            </div>
          )}

          <section style={{ marginTop: "1.5rem" }}>
            <h3 style={{ marginBottom: "0.5rem" }}>{hostnameRow.hostname}</h3>
            <table className="ui-table" style={{ width: "100%", maxWidth: "600px" }}>
              <tbody>
                <tr>
                  <th align="left">First detected</th>
                  <td>{hostnameRow.first_detected || "—"}</td>
                </tr>
                <tr>
                  <th align="left">Last detected</th>
                  <td>{hostnameRow.last_detected || "—"}</td>
                </tr>
                <tr>
                  <th align="left">Sessions</th>
                  <td>{hostnameRow.session_count ?? "—"}</td>
                </tr>
                <tr>
                  <th align="left">Event owners</th>
                  <td>{formatEventOwners(hostnameRow.event_owners)}</td>
                </tr>
                <tr>
                  <th align="left">Expected?</th>
                  <td>{hostnameRow.expected ? "Yes" : "No"}</td>
                </tr>
              </tbody>
            </table>
          </section>

          <section style={{ marginTop: "1.5rem", maxWidth: "600px" }}>
            <h4 style={{ marginBottom: "0.5rem" }}>Notes</h4>
            <textarea
              value={notesDraft}
              onChange={(e) => setNotesDraft(e.target.value)}
              rows={5}
              style={{ width: "100%" }}
            />
            {noteError && (
              <p style={{ color: "#b00020", marginTop: "0.5rem" }}>{noteError}</p>
            )}
            <button
              onClick={saveNotes}
              disabled={savingNotes || !canSaveNotes}
              style={{ marginTop: "0.5rem" }}
            >
              {savingNotes ? "Saving…" : "Save notes"}
            </button>
          </section>

          <section style={{ marginTop: "1.5rem" }}>
            <h4 style={{ marginBottom: "0.5rem" }}>Event breakdown (last 14 days)</h4>
            {eventsLoading ? (
              <p>Loading events…</p>
            ) : eventError ? (
              <p style={{ color: "#b00020" }}>{eventError}</p>
            ) : eventNote ? (
              <p>{eventNote}</p>
            ) : eventRows.length ? (
              <table className="ui-table"
                border="1"
                cellPadding="8"
                style={{ width: "100%", marginTop: "0.5rem" }}
              >
                <thead>
                  <tr>
                    <th>Event Owner</th>
                    <th>Event Name</th>
                    <th>Event Count</th>
                  </tr>
                </thead>
                <tbody>
                  {eventRows.map((row) => (
                    <tr
                      key={`${row.eventOwner || "(not set)"}__${row.eventName || "(not set)"}`}
                    >
                      <td>{row.eventOwner || "(not set)"}</td>
                      <td>{row.eventName || "(not set)"}</td>
                      <td>{formatNumber(row.eventCount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p>No events found for this hostname in the selected window.</p>
            )}
          </section>
        </>
      )}
    </div>
  )
}
